import { Box, Icon, Typography } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import {
  IconDefinition,
  faBirthdayCake,
  faClock,
  faHandshake,
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  getClassTypeIcon,
  useActivityString,
  usePriceDetailsString,
  useSelfPacedIconInfo,
} from "@outschool/ui-components-website";
import React from "react";

export type SearchActivityType = Pick<
  Activity,
  | "uid"
  | "user_uid"
  | "isClub"
  | "is_ongoing_weekly"
  | "size_min"
  | "size_max"
  | "hasTeacherSchedule"
  | "duration_weeks"
  | "weekly_meetings"
  | "duration_minutes"
  | "allows_recurring_payment"
  | "price_cents"
  | "age_min"
  | "age_max"
  | "subject"
  | "canEnroll"
  | "isFlexSchedule"
  | "isSelfPaced"
  | "isIncludedWithMembership"
> &
  Partial<Pick<Activity, "clubSection" | "priceCreditsPerMeeting">>;

interface PriceDetailsProps {
  activity: SearchActivityType;
  /** Display pricing in credits instead of currency */
  showCredits?: boolean;
  showFundingBadge?: boolean;
}

function IconBullet({
  icon,
  text,
  iconColor,
}: {
  icon: IconDefinition;
  text: string | null;
  iconColor?: string;
}) {
  return (
    <Box flex sx={{ textAlign: "left" }}>
      <Icon
        icon={icon}
        sx={{
          width: "1.25em",
          marginRight: "0.5em",
          color: iconColor || "inherit",
        }}
      />
      <Typography variant={"caption"}>{text}</Typography>
    </Box>
  );
}

/**
 *
 * Copy of the ListingDetails component, but with a different layout for the gallery experiment.
 *
 * Note: This component should always have the same height when rendered in a list of cards in the gallery experiment
 * because whether to show the public funding badge or not is a global filter that is applied to all cards.
 */
export default function ListingDetails({
  activity,
  showFundingBadge = false,
}: PriceDetailsProps) {
  const { ageRangeString } = useActivityString();
  const { classTypeString, detailedScheduleString } = usePriceDetailsString();
  const classTypeIcon = getClassTypeIcon(activity);
  const selfPacedIconInfo = useSelfPacedIconInfo(activity);
  const { t } = useTranslation("client\\components\\Chips\\FreeForMembersChip");

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        flex
        sx={{
          flexDirection: "column",
          height: "100%",
          alignItems: "start",
          gap: "8px",
        }}
      >
        <IconBullet icon={classTypeIcon} text={classTypeString(activity)} />
        <IconBullet icon={faBirthdayCake} text={ageRangeString(activity)} />
        {activity.isSelfPaced ? (
          <IconBullet
            icon={selfPacedIconInfo.weeksOfFeedback.icon}
            text={selfPacedIconInfo.weeksOfFeedback.text}
          />
        ) : (
          <IconBullet icon={faClock} text={detailedScheduleString(activity)} />
        )}
        {showFundingBadge && (
          <IconBullet
            icon={faHandshake}
            text={t`State funding accepted`}
            iconColor="grey.600"
          />
        )}
      </Box>
    </Box>
  );
}
